import { createSlice,createAsyncThunk } from "@reduxjs/toolkit";
import { WeekTemplateService } from "../../services/weekTemplateService";
import logger from "../../core/utils/logger";

// 27.4: optional filters ({ name, status, validityCoversDate, includeArchived })
// are forwarded to the 26.5 list endpoint. Called with no arg it lists the full
// non-archived set (the default management view).
export const getUserWeekTemplateSchedulesInOrg=createAsyncThunk(
    'weekTemplate/getUserWeekTemplateSchedulesInOrg',
    async(filters, {rejectWithValue})=>{
        try{
            const response=await WeekTemplateService.getUserWeekTemplateSchedules(filters || {});
            logger.log("User week template schedules response:", JSON.stringify(response));
            return response;
        }catch(error){
            logger.error('Error:', error);
            // Check if the error is a 404 (Not Found)
      if (error.response && error.response.status === 404) {
        logger.warn("No week template schedules found, returning empty array.");
        return []; // Return an empty array instead of an error
      }

            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error getting week template schedules';
            throw new Error(errorMessage);
        }
    }


);
export const saveUserWeekTemplateSchedule=createAsyncThunk(
    'weekTemplate/saveUserWeekTemplateSchedule',
    async(userWeekTemplateSchedule,{rejectWithValue})=>{
        try{
            const response=await WeekTemplateService.saveUserWeekTemplateSchedule(userWeekTemplateSchedule);
            logger.log("User week template schedule response:", response);
            return response;
        }catch(error){
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error saving week template schedules';
            throw new Error(errorMessage);
        }
    }
);
export const deleteUserWeekTemplateSchedule=createAsyncThunk(
    'weekTemplate/deleteUserWeekTemplateSchedule',
    async(userWeekTemplateScheduleId,{rejectWithValue})=>{
        try{
            const response=await WeekTemplateService.deleteUserWeekTemplateSchedule(userWeekTemplateScheduleId);
            logger.log("User week template schedule response:", response);
            return response;
        }catch(error){
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error deleting week template schedules';
            throw new Error(errorMessage);
        }
    }
);
// Epic 27: per-template management thunks. They reject with the backend's
// structured `{ code, message }` body (when present) so the component can map
// the code to a localized message via templateErrors.js. Each mutation leaves
// the refresh to the caller (component re-fetches `getUserWeekTemplateSchedulesInOrg`
// after success or rejection so the list always matches server truth).
const rejectWithTemplateError = (error, rejectWithValue, fallback) => {
    logger.error('Error:', error);
    const data = error.response && error.response.data;
    if (data && typeof data === 'object') {
        return rejectWithValue(data); // { code, message }
    }
    const message =
        (data && data.message) || error.message || fallback;
    return rejectWithValue({ message });
};

export const createTemplateThunk=createAsyncThunk(
    'weekTemplate/createTemplate',
    async({employeeTemplateId, dto},{rejectWithValue})=>{
        try{
            return await WeekTemplateService.createTemplate(employeeTemplateId, dto);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error creating template');
        }
    }
);

export const updateTemplateThunk=createAsyncThunk(
    'weekTemplate/updateTemplate',
    async({templateId, dto},{rejectWithValue})=>{
        try{
            return await WeekTemplateService.updateTemplate(templateId, dto);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error updating template');
        }
    }
);

export const setTemplateStatusThunk=createAsyncThunk(
    'weekTemplate/setTemplateStatus',
    async({templateId, dto},{rejectWithValue})=>{
        try{
            return await WeekTemplateService.setTemplateStatus(templateId, dto);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error updating template status');
        }
    }
);

export const setTemplateDefaultThunk=createAsyncThunk(
    'weekTemplate/setTemplateDefault',
    async(templateId,{rejectWithValue})=>{
        try{
            return await WeekTemplateService.setTemplateDefault(templateId);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error setting default template');
        }
    }
);

export const archiveTemplatesThunk=createAsyncThunk(
    'weekTemplate/archiveTemplates',
    async(ids,{rejectWithValue})=>{
        try{
            return await WeekTemplateService.archiveTemplates(ids);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error archiving templates');
        }
    }
);

export const unarchiveTemplatesThunk=createAsyncThunk(
    'weekTemplate/unarchiveTemplates',
    async(ids,{rejectWithValue})=>{
        try{
            return await WeekTemplateService.unarchiveTemplates(ids);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error restoring templates');
        }
    }
);

export const deleteTemplateThunk=createAsyncThunk(
    'weekTemplate/deleteTemplate',
    async(templateId,{rejectWithValue})=>{
        try{
            return await WeekTemplateService.deleteTemplate(templateId);
        }catch(error){
            return rejectWithTemplateError(error, rejectWithValue, 'Error deleting template');
        }
    }
);

const initialState={
WeekTemplateData:{
    loading:false,
    error:null,
    weekTemplateSchedules:[]

}

};
const weekTemplateSlice=createSlice({
    name:'weekTemplate',
    initialState,
    reducers:{
        clearState:(state)=>{
            state.WeekTemplateData.loading=false;
            state.WeekTemplateData.error=null;
            state.WeekTemplateData.weekTemplateSchedules=[];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserWeekTemplateSchedulesInOrg.pending, (state) => {
                state.WeekTemplateData.loading = true;
                state.WeekTemplateData.error = null;
            })
            .addCase(getUserWeekTemplateSchedulesInOrg.fulfilled, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = null;
                
                // Debug: Check SubOrganisation data in Redux
                if (action.payload && action.payload.length > 0) {
                    const firstEmployee = action.payload[0];
                    if (firstEmployee.calendarTemplates && firstEmployee.calendarTemplates.length > 0) {
                        const firstCalendar = firstEmployee.calendarTemplates[0];
                        if (firstCalendar.weekPlan && firstCalendar.weekPlan.dailyPlans) {
                            const mondayPlan = firstCalendar.weekPlan.dailyPlans.find(dp => dp.day === "Monday");
                            if (mondayPlan) {
                                logger.log("[REDUX DEBUG] Monday plan in Redux:", mondayPlan);
                                logger.log("[REDUX DEBUG] Monday subOrganisationId:", mondayPlan.subOrganisationId);
                                logger.log("[REDUX DEBUG] Monday subOrganisation:", mondayPlan.subOrganisation);
                            }
                        }
                    }
                }
                
                state.WeekTemplateData.weekTemplateSchedules = action.payload;
            })
            .addCase(getUserWeekTemplateSchedulesInOrg.rejected, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = action.payload || 'Error getting user template schedules';
            })
            .addCase(saveUserWeekTemplateSchedule.pending, (state) => {
                state.WeekTemplateData.loading = true;
                state.WeekTemplateData.error = null;
            })
            .addCase(saveUserWeekTemplateSchedule.fulfilled, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = null;
                //state.WeekTemplateData.weekTemplateSchedules = action.payload;
            })
            .addCase(saveUserWeekTemplateSchedule.rejected, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = action.payload;
            })
            .addCase(deleteUserWeekTemplateSchedule.pending, (state) => {
                state.WeekTemplateData.loading = true;
                state.WeekTemplateData.error = null;
            })
            .addCase(deleteUserWeekTemplateSchedule.fulfilled, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = null;
               
            })
            .addCase(deleteUserWeekTemplateSchedule.rejected, (state, action) => {
                state.WeekTemplateData.loading = false;
                state.WeekTemplateData.error = action.payload;
            });
    }
});
export const {clearState}=weekTemplateSlice.actions;
export default weekTemplateSlice.reducer;